/**
 * SamvadLanding — संस्कृत युवा संवाद कार्यक्रम
 *
 * Program landing page for the Sanskrit Youth Communication Program.
 * Distinct from /studio (private journal). Parallel to Dhanurvidya (Archery Program).
 *
 * Sprint 1: static landing with 8-phase overview, 6 roles, and the full
 * /products book catalogue as immediately available study material.
 */
import '../styles/SamvadLanding.css';
import ShareableHeading from './ShareableHeading';
import { useGlossary } from '../contexts/GlossaryContext';

interface Props {
  onNavigate: (path: string) => void;
}

// ─── 8 phases from the program matrix ─────────────────────────────────────────

const PHASES = [
  {
    num: '॥ १ ॥',
    hi: 'प्रवेश एवं पंजीकरण',
    en: 'Onboarding & Registration',
    activities: 5,
    icon: '📋',
  },
  {
    num: '॥ २ ॥',
    hi: 'पाठ्यक्रम एवं पुस्तक विकास',
    en: 'Curriculum & Book Development',
    activities: 8,
    icon: '📚',
  },
  {
    num: '॥ ३ ॥',
    hi: 'युगलन एवं समय-निर्धारण',
    en: 'Partner Matching & Scheduling',
    activities: 4,
    icon: '🤝',
  },
  {
    num: '॥ ४ ॥',
    hi: 'सत्र संचालन',
    en: 'Live Session Execution',
    activities: 6,
    icon: '🎙️',
  },
  {
    num: '॥ ५ ॥',
    hi: 'व्याकरण एवं निरुक्ति-अभ्यास',
    en: 'Grammar & Etymology Study',
    activities: 4,
    icon: '📐',
  },
  {
    num: '॥ ६ ॥',
    hi: 'मूल्यांकन एवं प्रतिपुष्टि',
    en: 'Assessment & Feedback',
    activities: 5,
    icon: '📊',
  },
  {
    num: '॥ ७ ॥',
    hi: 'समुदाय एवं कार्यक्रम',
    en: 'Community & Events',
    activities: 5,
    icon: '🏛️',
  },
  {
    num: '॥ ८ ॥',
    hi: 'प्रमाणपत्र एवं सम्मान',
    en: 'Certification & Recognition',
    activities: 4,
    icon: '🏅',
  },
] as const;

// ─── 6 roles from the program matrix ──────────────────────────────────────────

const ROLES = [
  {
    icon: '⚙️',
    hi: 'प्रशासक',
    en: 'Administrator',
    accent: 'gray',
    responsibilities: [
      'पंजीकरण, युगलन एवं कार्यक्रम-समन्वय',
      'प्रमाणपत्र जारी करना और डेटा प्रबंधन',
    ],
  },
  {
    icon: '📖',
    hi: 'शिष्य',
    en: 'Student (Beginner – Intermediate)',
    accent: 'blue',
    responsibilities: [
      'युगल वीडियो सत्रों में भागीदारी',
      'पुस्तक अध्ययन, संवाद अभ्यास, घण्टा अभिलेखन',
    ],
  },
  {
    icon: '🌟',
    hi: 'वरिष्ठ शिष्य',
    en: 'Senior Student (Advanced)',
    accent: 'green',
    responsibilities: [
      'सहपाठी समूहों का नेतृत्व और समन्वय',
      'कनिष्ठ शिष्यों का मार्गदर्शन; संवाद आदर्श प्रस्तुत करना',
    ],
  },
  {
    icon: '📐',
    hi: 'उपाध्याय',
    en: 'Upadhyaya (Subject Teacher)',
    accent: 'amber',
    responsibilities: [
      'पाणिनीय व्याकरण, निरुक्ति, निघण्टु का शिक्षण',
      'पुस्तक-टिप्पणी लेखन और सत्रोपरान्त प्रतिपुष्टि',
    ],
  },
  {
    icon: '🏛️',
    hi: 'आचार्य',
    en: 'Acharya (Curriculum Master)',
    accent: 'purple',
    responsibilities: [
      'पाठ्यक्रम रचना और वैदिक ग्रन्थ-चयन',
      'पुस्तक विकास पर्यवेक्षण और मूल्यांकन-नियंत्रण',
    ],
  },
  {
    icon: '🪔',
    hi: 'गुरु',
    en: 'Guru (Traditional Authority)',
    accent: 'saffron',
    responsibilities: [
      'समग्र दृष्टि और शास्त्रीय प्रामाणिकता',
      'स्रोत-ग्रन्थ वैधता और अन्तिम उपाधि-प्रदान',
    ],
  },
] as const;

// ─── Books from /products — full grantha catalogue ────────────────────────────
// All books are already live at their internalPath; no backend endpoint needed.

const BOOKS = [
  {
    id: 'arya-rahasyamayi-guha',
    path: '/products/arya-rahasyamayi-guha',
    titleHi: 'आर्य और रहस्यमयी गुहा',
    subtitleHi: 'विशेष्यनिघ्न · गुहा-अन्वेषण',
    en: 'Ārya and the Mysterious Cave',
    desc: '३–८ वर्ष · १७ शक्ति-शब्द · अमरकोश २०३७–२०४०',
    img: '/images/cover_arya_rahasyamayi_guha.jpg',
    level: 'beginner' as const,
    levelHi: 'प्रारम्भिक',
    grammar: 'विशेषण · अमरकोश',
    isLatest: true,
  },
  {
    id: 'arya-svarnamanjusha',
    path: '/products/arya-svarnamanjusha',
    titleHi: 'आर्यः स्वर्णमञ्जूषा च',
    subtitleHi: 'विशेष्यनिघ्न · स्वर्ण-पेटिका',
    en: 'Ārya and the Golden Casket',
    desc: '३–८ वर्ष · १५ शक्ति-शब्द · अमरकोश २०३३–२०३६',
    img: '/images/cover_arya_svarnamanjusha.jpg',
    level: 'beginner' as const,
    levelHi: 'प्रारम्भिक',
    grammar: 'विशेषण · अमरकोश',
  },
  {
    id: 'arya-laghukhaga',
    path: '/products/arya-laghukhaga',
    titleHi: 'आर्यः लघुखगः च',
    subtitleHi: 'विशेष्यनिघ्न · पक्षी-बचाव',
    en: 'Ārya and the Little Bird',
    desc: '३–८ वर्ष · १५ शक्ति-शब्द · अमरकोश २०३०–२०३२',
    img: '/images/cover_arya_laghukhaga.jpg',
    level: 'beginner' as const,
    levelHi: 'प्रारम्भिक',
    grammar: 'विशेषण · अमरकोश',
  },
  {
    id: 'nrivarg',
    path: '/products/nrivarg',
    titleHi: 'नृवर्ग — अमरकोश',
    subtitleHi: 'रक्कड़ गाँव संवाद',
    en: 'Nṛvarga — Rakkad Village Dialogue',
    desc: 'पाणिनीय कारक · यास्क निरुक्ति · हिन्दी-संस्कृत संवाद',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778513728/cover_nrivarg_elhtq0.png',
    level: 'beginner' as const,
    levelHi: 'प्रारम्भिक',
    grammar: 'कारक · निरुक्ति',
  },
  {
    id: 'kshatriyavarg',
    path: '/products/kshatriyavarg',
    titleHi: 'क्षत्रियवर्ग — अमरकोश',
    subtitleHi: 'वेद लक्षणा गौ संवाद',
    en: 'Kṣatriyavarga — Gau Vaṃśa Dialogue',
    desc: 'राजा, चक्रवर्ती, सेना — हिन्दी-संस्कृत संवाद',
    img: 'https://storage.googleapis.com/raajadharma-books/images/cover_kshatriyavarg.png',
    level: 'intermediate' as const,
    levelHi: 'मध्यम',
    grammar: 'कारक · पाणिनि',
  },
  {
    id: 'vaishyavarg-khand1',
    path: '/products/vaishyavarg-khand1',
    titleHi: 'गोवर्धन — खण्ड १',
    subtitleHi: 'बीज · वैश्यवर्ग',
    en: 'Govardhana Vol. 1 · Bīja',
    desc: 'वैश्यवर्ग श्लोक १–६ · गुरु-शिष्य परंपरा',
    img: 'https://storage.googleapis.com/raajadharma-books/images/cover_vaishyavarg_khand1.jpg',
    level: 'beginner' as const,
    levelHi: 'प्रारम्भिक',
    grammar: 'श्लोक · अमरकोश',
    isLatest: true,
  },
  {
    id: 'vaishyavarg-khand2',
    path: '/products/vaishyavarg-khand2',
    titleHi: 'गोवर्धन — खण्ड २',
    subtitleHi: 'अङ्कुर · वैश्यवर्ग',
    en: 'Govardhana Vol. 2 · Aṅkura',
    desc: 'वैश्यवर्ग श्लोक ७–११ · गोशाला एवं उद्यम',
    img: 'https://storage.googleapis.com/raajadharma-books/images/cover_vaishyavarg_khand2.jpg',
    level: 'intermediate' as const,
    levelHi: 'मध्यम',
    grammar: 'श्लोक · अमरकोश',
  },
  {
    id: 'vaishyavarg-khand3',
    path: '/products/vaishyavarg-khand3',
    titleHi: 'गोवर्धन — खण्ड ३',
    subtitleHi: 'फल · वैश्यवर्ग',
    en: 'Govardhana Vol. 3 · Phala',
    desc: 'वैश्यवर्ग श्लोक १२–१८ · गीता १८.४४',
    img: 'https://storage.googleapis.com/raajadharma-books/images/cover_vaishyavarg_khand3.jpg',
    level: 'advanced' as const,
    levelHi: 'उन्नत',
    grammar: 'श्लोक · गीता',
  },
  {
    id: 'nishedha-madira',
    path: '/products/nishedha-madira',
    titleHi: 'निषेध-मदिरा',
    subtitleHi: 'उमा-महेश संवाद',
    en: 'Niṣedha-Madirā Dialogue',
    desc: 'महेश–उमा संवाद · १३ श्लोक · पाणिनि विश्लेषण',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778513650/cover_nishedha_madira_y84b0u.png',
    level: 'intermediate' as const,
    levelHi: 'मध्यम',
    grammar: 'संवाद · पुराण',
  },
  {
    id: 'vyaghra-valmiki',
    path: '/products/vyaghra-valmiki',
    titleHi: 'व्याघ्र की महिमाएँ',
    subtitleHi: 'अमरकोश · वाल्मीकि',
    en: 'Glory of Tigers — Amarakosha',
    desc: 'भाई-बहन हिंदी-संस्कृत संवाद · मधुबनी चित्र',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1777591349/cover-tiger__upscayl_2x_upscayl-lite-4x_uskukx.png',
    level: 'beginner' as const,
    levelHi: 'प्रारम्भिक',
    grammar: 'नाम-पद · निरुक्त',
  },
  {
    id: 'vaanara-samvad',
    path: '/products/vaanara-samvad',
    titleHi: 'वानर-संवाद',
    subtitleHi: 'अमरकोश · दरभंगा घाट',
    en: 'Vānara Saṃvāda — Amarakosha',
    desc: 'तुलसीदास–हनुमान संवाद · १० वानर-नाम',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1777882614/back-cover_den5m3.png',
    level: 'intermediate' as const,
    levelHi: 'मध्यम',
    grammar: 'नाम-पद · रामायण',
  },
  {
    id: 'varaaha-samvad',
    path: '/products/varaaha-samvad',
    titleHi: 'वाराह-संवाद',
    subtitleHi: 'अमरकोश',
    en: 'Varāha Saṃvāda — Amarakosha',
    desc: 'हनुमत्-भक्त संवाद · १२ वाराह-नाम',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1777734567/varaha-cover_upscayl_2x_upscayl-lite-4x_ycak4r.png',
    level: 'intermediate' as const,
    levelHi: 'मध्यम',
    grammar: 'नाम-पद · निरुक्त',
  },
  {
    id: 'saagar-manthan',
    path: '/products/saagar-manthan',
    titleHi: 'सागर-मंथन',
    subtitleHi: 'महाभारत आदिपर्व',
    en: 'Sāgara-Manthana',
    desc: 'क्षीरसागर मन्थन · १५ दृश्य · संस्कृत श्लोक',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778510960/cover_saagar_manthan_tijhhx.png',
    level: 'intermediate' as const,
    levelHi: 'मध्यम',
    grammar: 'श्लोक-पाठ · महाभारत',
  },
  {
    id: 'geedh',
    path: '/products/geedh',
    titleHi: 'गीधकी-आँख',
    subtitleHi: 'द्रोणगुरुकुल · एकलव्य',
    en: 'Gīdhakī-Āṃkha — Eye of the Kite',
    desc: 'अर्जुन–एकलव्य · ८० श्लोक · महाभारत',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778510877/cover_geedh_qrvtp5.png',
    level: 'advanced' as const,
    levelHi: 'उन्नत',
    grammar: 'श्लोक-पाठ · महाभारत',
  },
  {
    id: 'singh-amara',
    path: '/products/singh-amara',
    titleHi: 'सिंह की महिमाएँ',
    subtitleHi: 'अमरकोश',
    en: 'Glory of Lions — Amarakosha',
    desc: 'सिंहादिवर्ग · गिर वन · महाभारत श्लोक',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1777591906/Chitrakaya_qb14s7.png',
    level: 'beginner' as const,
    levelHi: 'प्रारम्भिक',
    grammar: 'नाम-पद · निरुक्त',
  },
  {
    id: 'hanuman-bahuk',
    path: '/products/hanuman-bahuk',
    titleHi: 'हनुमान बाहुक',
    subtitleHi: 'तुलसीदास · ४४ पद',
    en: 'Hanumān Bāhuk — Tulsidas',
    desc: 'अवधी भक्ति पद · हिंदी भावार्थ',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778513880/verse_01_sindhu_taran_tkg3qb.png',
    level: 'advanced' as const,
    levelHi: 'उन्नत',
    grammar: 'अवधी-काव्य',
  },
  {
    id: 'vanyagraamya-pashu',
    path: '/products/vanyagraamya-pashu',
    titleHi: 'वन्यग्राम्य पशु',
    subtitleHi: 'अमरकोश सिंहादिवर्ग',
    en: 'Wild and Village Animals',
    desc: 'मनुष्य–शिव–पशुपति · अमरकोश',
    img: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775694559/phpThumb_generated_thumbnail_upscayl_2x_upscayl-standard-4x_mfjwge.png',
    level: 'beginner' as const,
    levelHi: 'प्रारम्भिक',
    grammar: 'नाम-पद',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function SamvadLanding({ onNavigate }: Props) {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };

  return (
    <div className="sv-page">
      {/* ── Back to Products ── */}
      <button className="sv-back-btn" onClick={() => onNavigate('/products')}>
        {tl('samvad.back', '← उत्पाद')}
      </button>

      {/* ── Hero ── */}
      <div className="sv-hero">
        <span className="sv-hero-badge">{tl('samvad.hero.badge', 'संस्कृत युवा संवाद कार्यक्रम')}</span>
        <h1 className="sv-hero-title">
          {tl('samvad.hero.title.line1', 'Sanskrit Youth')}<br />
          {tl('samvad.hero.title.line2', 'Communication Program')}
        </h1>
        <p className="sv-hero-tagline">
          पाणिनीय व्याकरण · यास्क-निरुक्ति · निघण्टु · वैदिक साहित्य
        </p>
        <div className="sv-hero-meta">
          <span className="sv-meta-pill">{tl('samvad.meta.age', '🎂 आयु ९–२७')}</span>
          <span className="sv-meta-pill">{tl('samvad.meta.mode', '📍 ऑनलाइन + ऑफ़लाइन')}</span>
          <span className="sv-meta-pill">{tl('samvad.meta.activities', '📖 ४१ गतिविधियाँ')}</span>
          <span className="sv-meta-pill">{tl('samvad.meta.phases', '🏅 ८ चरण')}</span>
          <span className="sv-meta-pill">{tl('samvad.meta.roles', '👥 ६ भूमिकाएँ')}</span>
        </div>
        <div className="sv-hero-icon" aria-hidden>🗣️</div>
      </div>

      {/* ── Shloka ── */}
      <div className="sv-shloka-panel">
        <p className="sv-shloka">विद्या ददाति विनयम्</p>
        <p className="sv-shloka-meaning">{tl('samvad.shloka.meaning', 'Knowledge bestows humility')}</p>
      </div>

      {/* ── 8 Phases ── */}
      <div className="sv-section">
        <ShareableHeading id="charana" as="h2" className="sv-section-title">
          {tl('samvad.section.phases', '८ चरण — Eight Phases')}
        </ShareableHeading>
        <p className="sv-section-subtitle">
          {tl('samvad.section.phases.sub', 'प्रवेश से प्रमाणपत्र तक — From Onboarding to Certification')}
        </p>
        <div className="sv-phases">
          {PHASES.map((p) => (
            <div className="sv-phase-card" key={p.num}>
              <div className="sv-phase-num-wrap">
                <span className="sv-phase-icon">{p.icon}</span>
                <span className="sv-phase-num">{p.num}</span>
              </div>
              <div className="sv-phase-body">
                <h3 className="sv-phase-title-hi">{p.hi}</h3>
                <p className="sv-phase-title-en">{p.en}</p>
                <span className="sv-phase-acts">{p.activities} {tl('samvad.activities', 'गतिविधियाँ')}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 6 Roles ── */}
      <div className="sv-section">
        <ShareableHeading id="bhumika" as="h2" className="sv-section-title">
          {tl('samvad.section.roles', '६ भूमिकाएँ — Six Roles')}
        </ShareableHeading>
        <p className="sv-section-subtitle">
          {tl('samvad.section.roles.sub', 'प्रत्येक भूमिका की जिम्मेदारियाँ स्पष्ट और द्विआङ्कीय हैं')}
        </p>
        <div className="sv-roles">
          {ROLES.map((r) => (
            <div className={`sv-role-card sv-role--${r.accent}`} key={r.hi}>
              <div className="sv-role-header">
                <span className="sv-role-icon">{r.icon}</span>
                <div>
                  <h3 className="sv-role-name-hi">{r.hi}</h3>
                  <p className="sv-role-name-en">{r.en}</p>
                </div>
              </div>
              <ul className="sv-role-resp">
                {r.responsibilities.map((resp) => (
                  <li key={resp}>{resp}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── Books / Study Material ── */}
      <div className="sv-section">
        <ShareableHeading id="path-samagri" as="h2" className="sv-section-title">
          {tl('samvad.section.books', 'पाठ सामग्री — Study Books')}
        </ShareableHeading>
        <p className="sv-section-subtitle">
          {tl('samvad.section.books.sub',
            'कार्यक्रम में उपयोग की जाने वाली पुस्तकें — पाणिनीय व्याकरण, निरुक्ति, और वैदिक संवाद · सभी निःशुल्क'
          )}
        </p>
        <div className="sv-books">
          {BOOKS.map((b) => (
            <div
              className="sv-book-card"
              key={b.id}
              onClick={() => onNavigate(b.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onNavigate(b.path); }}
            >
              {b.isLatest && <span className="sv-book-latest-badge">{tl('samvad.book.latest', 'नवीनतम')}</span>}
              <div className="sv-book-cover-wrap">
                <img
                  src={b.img}
                  alt={b.titleHi}
                  className="sv-book-cover"
                  loading="lazy"
                />
              </div>
              <div className="sv-book-info">
                <div className="sv-book-title-hi">{b.titleHi}</div>
                <div className="sv-book-subtitle">{b.subtitleHi}</div>
                <div className="sv-book-desc">{b.desc}</div>
                <div className="sv-book-meta">
                  <span className={`sv-level sv-level--${b.level}`}>{b.levelHi}</span>
                  <span className="sv-book-grammar">{b.grammar}</span>
                </div>
              </div>
              <div className="sv-book-cta-row">
                <button
                  className="sv-book-read-btn"
                  onClick={(e) => { e.stopPropagation(); onNavigate(b.path); }}
                >
                  {tl('samvad.book.read', '📖 पढ़ें')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Prerequisites ── */}
      <div className="sv-section">
        <ShareableHeading id="paatra" as="h2" className="sv-section-title">
          {tl('samvad.section.prereqs', 'पात्रता — Prerequisites')}
        </ShareableHeading>
        <div className="sv-prereqs">
          {[
            { icon: '🎂', textKey: 'samvad.prereq.age',      fallback: 'आयु ९ से २७ वर्ष' },
            { icon: '📖', textKey: 'samvad.prereq.devanagari', fallback: 'हिन्दी पठन क्षमता (देवनागरी लिपि)' },
            { icon: '🌐', textKey: 'samvad.prereq.internet',  fallback: 'वीडियो कॉल हेतु इन्टरनेट और उपकरण' },
            { icon: '📍', textKey: 'samvad.prereq.pincode',   fallback: 'पिन-कोड प्रविष्टि (स्थानीय युगलन हेतु)' },
            { icon: '⏱️', textKey: 'samvad.prereq.time',     fallback: 'प्रति सप्ताह कम से कम १ घण्टा' },
            { icon: '👨‍👩‍👧', textKey: 'samvad.prereq.guardian', fallback: 'आयु १८ से कम हेतु अभिभावक सहमति' },
          ].map((item) => (
            <div className="sv-prereq-item" key={item.textKey}>
              <span className="sv-prereq-icon">{item.icon}</span>
              <span>{tl(item.textKey, item.fallback)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Titles pathway ── */}
      <div className="sv-section">
        <ShareableHeading id="upadhi" as="h2" className="sv-section-title">
          {tl('samvad.section.titles', 'उपाधि-पथ — Title Pathway')}
        </ShareableHeading>
        <p className="sv-section-subtitle">
          {tl('samvad.section.titles.sub',
            'घण्टे, मूल्यांकन, और आचार्य–गुरु प्रमाणन से प्राप्त होने वाली उपाधियाँ'
          )}
        </p>
        <div className="sv-titles">
          {[
            { title: 'प्रथमा',  hint: '₁', hours: tl('samvad.title.hours1', '≥ १० घण्टे'),  en: 'Prathamā' },
            { title: 'द्वितीया', hint: '₂', hours: tl('samvad.title.hours2', '≥ २५ घण्टे'), en: 'Dvitīyā' },
            { title: 'तृतीया',  hint: '₃', hours: tl('samvad.title.hours3', '≥ ५० घण्टे'),  en: 'Tṛtīyā' },
            { title: 'विशारद', hint: '★', hours: tl('samvad.title.hours4', '≥ १०० घण्टे'), en: 'Viśārada' },
          ].map((item) => (
            <div className="sv-title-step" key={item.title}>
              <div className="sv-title-hint">{item.hint}</div>
              <div className="sv-title-name">{item.title}</div>
              <div className="sv-title-en">{item.en}</div>
              <div className="sv-title-hours">{item.hours}</div>
            </div>
          ))}
        </div>
        <p className="sv-titles-note">
          {tl('samvad.titles.note', '* उपाधि प्रदान — केवल आचार्य एवं गुरु द्वारा। मंच द्वारा स्वतः नहीं।')}
        </p>
      </div>

      {/* ── CTA ── */}
      <div className="sv-cta-panel">
        <h2>{tl('samvad.cta.heading', 'कार्यक्रम में प्रवेश लें')}</h2>
        <p>
          {tl('samvad.cta.body',
            'संस्कृत संवाद अभ्यास, पाणिनीय व्याकरण और यास्क-निरुक्ति के माध्यम से अपनी भाषा-क्षमता को शास्त्रीय आधार दें।'
          )}
        </p>
        <div className="sv-cta-buttons">
          <button
            className="sv-btn-primary"
            onClick={() => onNavigate('/samvad/onboarding')}
          >
            {tl('samvad.cta.register', '📋 पंजीकरण करें')}
          </button>
          <button
            className="sv-btn-secondary"
            onClick={() => onNavigate('/samvad/dashboard')}
          >
            {tl('samvad.cta.dashboard', '📊 मेरा डैशबोर्ड')}
          </button>
          <button
            className="sv-btn-secondary"
            onClick={() => onNavigate('/samvad/session-log')}
          >
            {tl('samvad.cta.log', '📝 सत्र लॉग करें')}
          </button>
          <button
            className="sv-btn-secondary"
            onClick={() => onNavigate('/products')}
          >
            {tl('samvad.cta.books', '📦 सभी पुस्तकें')}
          </button>
        </div>
        <p className="sv-cta-note">
          {tl('samvad.cta.note', 'शीघ्र ही: पिन-कोड-आधारित युगलन और स्व-मूल्यांकन डैशबोर्ड।')}
        </p>
      </div>

    </div>
  );
}
